import type { ReactElement, ReactNode } from 'react';
import { CheckCircle } from 'lucide-react';
import ImagePlaceholder from './ImagePlaceholder';

interface TourSectionBadge {
  text: string;
  variant: 'orange' | 'indigo' | 'green';
}

interface TourSectionProps {
  title: string;
  description: string;
  features?: string[];
  imagePosition?: 'left' | 'right';
  imagePlaceholder: string;
  imageAspectRatio?: '16/9' | '4/3' | '1/1';
  imageVariant?: 'screenshot' | 'video';
  imageSrcLight?: string;
  imageSrcDark?: string;
  imageObjectFit?: 'cover' | 'contain';
  badge?: TourSectionBadge;
  background?: 'white' | 'cream' | 'gradient';
  children?: ReactNode;
}

const TourSection = ({
  title,
  description,
  features = [],
  imagePosition = 'right',
  imagePlaceholder,
  imageAspectRatio = '16/9',
  imageVariant = 'screenshot',
  imageSrcLight,
  imageSrcDark,
  imageObjectFit = 'cover',
  badge,
  background = 'white',
  children
}: TourSectionProps): ReactElement => {
  const badgeColors = {
    orange: {
      bg: 'var(--st-border-light)',
      text: 'var(--st-primary)'
    },
    indigo: {
      bg: '#E0E7FF',
      text: '#4F46E5'
    },
    green: {
      bg: '#DCFCE7',
      text: '#22C55E'
    }
  };

  const backgroundStyles = {
    white: { background: 'var(--st-bg-surface)' },
    cream: { background: 'var(--st-bg-page)' },
    gradient: {
      background: 'linear-gradient(135deg, var(--st-bg-page) 0%, var(--st-border-light) 100%)'
    }
  };

  const imageContent = (
    <div className="flex-1">
      <ImagePlaceholder
        label={imagePlaceholder}
        aspectRatio={imageAspectRatio}
        variant={imageVariant}
        imageSrcLight={imageSrcLight}
        imageSrcDark={imageSrcDark}
        objectFit={imageObjectFit}
      />
    </div>
  );

  const textContent = (
    <div className="flex-1 space-y-4">
      {badge && (
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: badgeColors[badge.variant].bg }}
        >
          <span
            className="text-xs font-bold tracking-wide"
            style={{ color: badgeColors[badge.variant].text, fontFamily: 'Quicksand, sans-serif' }}
          >
            {badge.text}
          </span>
        </div>
      )}

      <h2
        className="text-2xl md:text-3xl font-bold"
        style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
      >
        {title}
      </h2>

      <p
        className="text-base leading-relaxed"
        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
      >
        {description}
      </p>

      {features.length > 0 && (
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <CheckCircle
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: '#22C55E' }}
              />
              <span
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>
      )}

      {children}
    </div>
  );

  return (
    <section className="py-16 px-4" style={backgroundStyles[background]}>
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col ${imagePosition === 'left' ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-10`}>
          {textContent}
          {imageContent}
        </div>
      </div>
    </section>
  );
};

export default TourSection;
